import React, { useEffect, useMemo, useState } from "react";
import { Ban, Copy, Mail, RefreshCcw, ShieldCheck, ShieldOff, UserCog } from "lucide-react";
import { API_BASE_URL } from "../api";
import { Button, Field, SectionHeader, SurfaceCard } from "../components/designSystem";
import { DEFAULT_USD_NGN_RATE, formatNaira, formatUsd } from "../utils/currencyDisplay";

function AdminTools() {
  const [input, setInput] = useState({ username: "", email: "" });
  const [rateInput, setRateInput] = useState(String(DEFAULT_USD_NGN_RATE));
  const [rateInfo, setRateInfo] = useState({ rate: DEFAULT_USD_NGN_RATE, source: "fallback", updatedAt: null });
  const [loading, setLoading] = useState(false);
  const [loadingRate, setLoadingRate] = useState(true);
  const [savingRate, setSavingRate] = useState(false);
  const [success, setSuccess] = useState("");
  const [error, setError] = useState("");
  const [rateSuccess, setRateSuccess] = useState("");
  const [rateError, setRateError] = useState("");
  const [identitySupportInput, setIdentitySupportInput] = useState({ identityId: "", mekedgeUserId: "", activateDisabled: false });
  const [entitlementSupportInput, setEntitlementSupportInput] = useState({ entitlementId: "", email: "", claimTtlHours: "168" });
  const [mekFundedLoading, setMekFundedLoading] = useState(false);
  const [mekFundedSuccess, setMekFundedSuccess] = useState("");
  const [mekFundedError, setMekFundedError] = useState("");
  const [mintedClaim, setMintedClaim] = useState(null);
  const [importVisibility, setImportVisibility] = useState(null);
  const [importVisibilityLoading, setImportVisibilityLoading] = useState(false);
  const [importVisibilityError, setImportVisibilityError] = useState("");
  const [visibilityQuery, setVisibilityQuery] = useState("");
  const [visibilityStatus, setVisibilityStatus] = useState("all");
  const [visibilityPages, setVisibilityPages] = useState({ entitlements: 1, trades: 1, audits: 1 });
  const [auditActionFilter, setAuditActionFilter] = useState("");
  const [auditOutcomeFilter, setAuditOutcomeFilter] = useState("all");
  const [auditCustomerFilter, setAuditCustomerFilter] = useState("");
  const VISIBILITY_PAGE_SIZE = 4;
  const AUDIT_PAGE_SIZE = 6;

  useEffect(() => {
    let active = true;

    async function loadRate() {
      setLoadingRate(true);
      setRateError("");
      const token = localStorage.getItem("token");

      try {
        const response = await fetch(`${API_BASE_URL}/api/admin/exchange-rate`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
        const data = await response.json();

        if (!response.ok) {
          throw new Error(data?.error || "Failed to load exchange rate.");
        }

        if (active) {
          const nextRate = Number(data?.rate) || DEFAULT_USD_NGN_RATE;
          setRateInfo({
            rate: nextRate,
            source: data?.source || "live",
            updatedAt: data?.updatedAt || null,
          });
          setRateInput(String(nextRate));
        }
      } catch (loadError) {
        if (active) {
          setRateError(loadError.message || "Failed to load exchange rate.");
        }
      } finally {
        if (active) {
          setLoadingRate(false);
        }
      }
    }

    loadRate();

    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    let active = true;

    async function loadImportVisibility() {
      setImportVisibilityLoading(true);
      setImportVisibilityError("");

      const token = localStorage.getItem("token");
      try {
        const response = await fetch(`${API_BASE_URL}/api/mekfunded/admin/import-visibility`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
        const data = await response.json().catch(() => null);

        if (!response.ok) {
          throw new Error(data?.error || "Failed to load import visibility.");
        }

        if (active) {
          setImportVisibility(data);
        }
      } catch (loadError) {
        if (active) {
          setImportVisibilityError(loadError.message || "Failed to load import visibility.");
        }
      } finally {
        if (active) {
          setImportVisibilityLoading(false);
        }
      }
    }

    loadImportVisibility();

    return () => {
      active = false;
    };
  }, []);

  const submit = async (event) => {
    event.preventDefault();
    setSuccess("");
    setError("");
    setLoading(true);
    const token = localStorage.getItem("token");

    try {
      const res = await fetch(`${API_BASE_URL}/api/set-admin`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(input)
      });
      const data = await res.json();
      if (res.ok && data.success) {
        setSuccess(`User ${data.user.username} is now an admin.`);
        setInput({ username: "", email: "" });
      } else {
        setError(data.error || "Failed to set admin status.");
      }
    } catch {
      setError("Network error.");
    }

    setLoading(false);
  };

  const saveRate = async (event) => {
    event.preventDefault();
    setRateSuccess("");
    setRateError("");
    setSavingRate(true);

    const token = localStorage.getItem("token");

    try {
      const response = await fetch(`${API_BASE_URL}/api/admin/exchange-rate`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ rate: Number(rateInput) }),
      });
      const data = await response.json();

      if (!response.ok || !data.success) {
        throw new Error(data?.error || "Failed to update exchange rate.");
      }

      const nextRate = Number(data?.rate) || DEFAULT_USD_NGN_RATE;
      setRateInfo({
        rate: nextRate,
        source: data?.source || "manual",
        updatedAt: data?.updatedAt || new Date().toISOString(),
      });
      setRateInput(String(nextRate));
      setRateSuccess(`USD/NGN rate updated to ${nextRate.toLocaleString("en-NG", { maximumFractionDigits: 2 })}.`);
    } catch (saveError) {
      setRateError(saveError.message || "Failed to update exchange rate.");
    }

    setSavingRate(false);
  };

  const rateUpdatedLabel = rateInfo.updatedAt
    ? new Date(rateInfo.updatedAt).toLocaleString()
    : "Live provider rate";

  async function runMekFundedAction({ path, method = "PATCH", body, successMessage, fallbackSuccessMessage, onSuccess }) {
    setMekFundedLoading(true);
    setMekFundedError("");
    setMekFundedSuccess("");

    const token = localStorage.getItem("token");

    try {
      const response = await fetch(`${API_BASE_URL}${path}`, {
        method,
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: body ? JSON.stringify(body) : undefined,
      });
      const data = await response.json().catch(() => null);

      if (!response.ok) {
        throw new Error(data?.error || "MekFunded admin action failed.");
      }

      if (data?.delivery === "manual" && data?.claim?.url) {
        setMekFundedSuccess(`Mail is not configured. Manual claim link: ${data.claim.url}`);
      } else {
        setMekFundedSuccess(successMessage || data?.message || fallbackSuccessMessage || "MekFunded admin action completed.");
      }

      if (typeof onSuccess === "function") {
        onSuccess(data);
      }
    } catch (actionError) {
      setMekFundedError(actionError.message || "MekFunded admin action failed.");
    } finally {
      setMekFundedLoading(false);
    }
  }

  async function copyToClipboard(value, label) {
    try {
      await navigator.clipboard.writeText(value);
      setMekFundedSuccess(`${label} copied to clipboard.`);
      setMekFundedError("");
    } catch {
      setMekFundedError(`Failed to copy ${label.toLowerCase()}.`);
    }
  }

  const relinkIdentity = (event) => {
    event.preventDefault();
    runMekFundedAction({
      path: `/api/admin/mekfunded/identities/${identitySupportInput.identityId}/relink`,
      body: {
        mekedgeUserId: identitySupportInput.mekedgeUserId,
        activateDisabled: identitySupportInput.activateDisabled,
      },
      successMessage: "MekFunded identity relinked successfully.",
    });
  };

  const unlinkIdentity = () => runMekFundedAction({
    path: `/api/admin/mekfunded/identities/${identitySupportInput.identityId}/unlink`,
    successMessage: "MekFunded identity unlinked and sync suspended.",
  });

  const disableIdentity = () => runMekFundedAction({
    path: `/api/admin/mekfunded/identities/${identitySupportInput.identityId}/disable`,
    successMessage: "MekFunded identity disabled and linked entitlements suspended.",
  });

  const disableEntitlement = () => runMekFundedAction({
    path: `/api/admin/mekfunded/entitlements/${entitlementSupportInput.entitlementId}/disable`,
    successMessage: "Entitlement disabled successfully.",
  });

  const revokeEntitlement = () => runMekFundedAction({
    path: `/api/admin/mekfunded/entitlements/${entitlementSupportInput.entitlementId}/revoke`,
    successMessage: "Entitlement revoked successfully.",
  });

  const sendClaimEmail = () => runMekFundedAction({
    path: `/api/admin/mekfunded/entitlements/${entitlementSupportInput.entitlementId}/send-claim-email`,
    method: "POST",
    body: {
      email: entitlementSupportInput.email,
      claimTtlHours: Number(entitlementSupportInput.claimTtlHours || 168),
    },
    successMessage: entitlementSupportInput.email
      ? `Claim link delivered to ${entitlementSupportInput.email}.`
      : "Claim link delivered successfully.",
    fallbackSuccessMessage: "Claim link prepared successfully.",
    onSuccess: () => setMintedClaim(null),
  });

  const mintClaimLink = () => runMekFundedAction({
    path: `/api/admin/mekfunded/entitlements/${entitlementSupportInput.entitlementId}/mint-claim-link`,
    method: "POST",
    body: {
      claimTtlHours: Number(entitlementSupportInput.claimTtlHours || 168),
    },
    successMessage: "Claim link minted. Share this with MekFunded for external email delivery.",
    onSuccess: (data) => {
      if (data?.claim?.url) {
        setMintedClaim(data.claim);
      }
    },
  });

  const reloadImportVisibility = async () => {
    setImportVisibilityLoading(true);
    setImportVisibilityError("");

    const token = localStorage.getItem("token");
    try {
      const response = await fetch(`${API_BASE_URL}/api/mekfunded/admin/import-visibility`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      const data = await response.json().catch(() => null);

      if (!response.ok) {
        throw new Error(data?.error || "Failed to load import visibility.");
      }

      setImportVisibility(data);
    } catch (loadError) {
      setImportVisibilityError(loadError.message || "Failed to load import visibility.");
    } finally {
      setImportVisibilityLoading(false);
    }
  };

  const visibilityQueryValue = visibilityQuery.trim().toLowerCase();
  const filteredEntitlements = useMemo(() => {
    const entitlements = importVisibility?.entitlements || [];
    return entitlements.filter((item) => {
      const matchesStatus = visibilityStatus === "all" || item.status === visibilityStatus;
      const haystack = [item.mekFundedCustomerId, item.mt5Login, item.mt5Server, item.status, item.broker]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      const matchesQuery = !visibilityQueryValue || haystack.includes(visibilityQueryValue);
      return matchesStatus && matchesQuery;
    });
  }, [importVisibility, visibilityQueryValue, visibilityStatus]);

  const filteredTrades = useMemo(() => {
    const trades = importVisibility?.importedTrades || [];
    return trades.filter((item) => {
      const haystack = [item.asset, item.externalAccountLogin, item.externalPositionId, item.externalServer, item.source]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return !visibilityQueryValue || haystack.includes(visibilityQueryValue);
    });
  }, [importVisibility, visibilityQueryValue]);

  const filteredAudits = useMemo(() => {
    const logs = importVisibility?.auditLogs || [];
    return logs.filter((item) => {
      const haystack = [item.action, item.outcome, item.actorType, item.mekFundedCustomerId, item.message]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      const matchesQuery = !visibilityQueryValue || haystack.includes(visibilityQueryValue);
      const matchesAction = !auditActionFilter.trim() || String(item.action || "").toLowerCase().includes(auditActionFilter.trim().toLowerCase());
      const matchesOutcome = auditOutcomeFilter === "all" || String(item.outcome || "").toLowerCase() === auditOutcomeFilter.toLowerCase();
      const matchesCustomer = !auditCustomerFilter.trim() || String(item.mekFundedCustomerId || "").toLowerCase().includes(auditCustomerFilter.trim().toLowerCase());
      return matchesQuery && matchesAction && matchesOutcome && matchesCustomer;
    });
  }, [importVisibility, visibilityQueryValue, auditActionFilter, auditOutcomeFilter, auditCustomerFilter]);

  const visibleEntitlements = filteredEntitlements.slice((visibilityPages.entitlements - 1) * VISIBILITY_PAGE_SIZE, visibilityPages.entitlements * VISIBILITY_PAGE_SIZE);
  const visibleTrades = filteredTrades.slice((visibilityPages.trades - 1) * VISIBILITY_PAGE_SIZE, visibilityPages.trades * VISIBILITY_PAGE_SIZE);
  const visibleAudits = filteredAudits.slice((visibilityPages.audits - 1) * AUDIT_PAGE_SIZE, visibilityPages.audits * AUDIT_PAGE_SIZE);

  function setPage(section, nextPage) {
    setVisibilityPages((current) => ({
      ...current,
      [section]: Math.max(1, nextPage),
    }));
  }

  return (
    <div className="mx-auto max-w-4xl space-y-6">
      <SurfaceCard className="overflow-hidden bg-[radial-gradient(circle_at_top_left,_rgba(244,63,94,0.14),_transparent_22%),linear-gradient(135deg,_#ffffff,_#f8fafc)] dark:bg-[radial-gradient(circle_at_top_left,_rgba(244,63,94,0.12),_transparent_22%),linear-gradient(135deg,_#111827,_#0f172a)]">
        <SectionHeader eyebrow="Admin Tools" title="Grant admin access" description="Promote a user to admin by username or email. One valid identifier is enough for the backend lookup." />
      </SurfaceCard>

      {error ? <SurfaceCard><p className="text-sm font-medium text-rose-600 dark:text-rose-300" role="alert">{error}</p></SurfaceCard> : null}
      {success ? <SurfaceCard><p className="text-sm font-medium text-emerald-600 dark:text-emerald-300" role="status">{success}</p></SurfaceCard> : null}
      {rateError ? <SurfaceCard><p className="text-sm font-medium text-rose-600 dark:text-rose-300" role="alert">{rateError}</p></SurfaceCard> : null}
      {rateSuccess ? <SurfaceCard><p className="text-sm font-medium text-emerald-600 dark:text-emerald-300" role="status">{rateSuccess}</p></SurfaceCard> : null}
      {mekFundedError ? <SurfaceCard><p className="text-sm font-medium text-rose-600 dark:text-rose-300" role="alert">{mekFundedError}</p></SurfaceCard> : null}
      {mekFundedSuccess ? <SurfaceCard><p className="text-sm font-medium text-emerald-600 dark:text-emerald-300 break-all" role="status">{mekFundedSuccess}</p></SurfaceCard> : null}

      <div className="grid gap-6 xl:grid-cols-[0.95fr_1.05fr]">
        <SurfaceCard>
          <SectionHeader eyebrow="Promotion" title="Set a user as admin" description="Provide either a username or an email. The backend applies the configured admin privileges after lookup succeeds." />
          <form className="mt-6 space-y-5" onSubmit={submit}>
            <Field label="Username" type="text" placeholder="Username (optional)" value={input.username} onChange={e => setInput({ ...input, username: e.target.value })} />
            <Field label="Email" type="email" placeholder="Email (optional)" value={input.email} onChange={e => setInput({ ...input, email: e.target.value })} />
            <Button type="submit" variant="danger" className="w-full" disabled={loading}>
              <UserCog className="h-4 w-4" />
              {loading ? "Processing..." : "Set as Admin"}
            </Button>
          </form>
        </SurfaceCard>

        <SurfaceCard>
          <SectionHeader eyebrow="Exchange rate" title="Control the NGN conversion" description="All USD-first money cards can use this USD/NGN rate so the naira line matches your current operating rate." />
          <div className="mt-6 rounded-3xl border border-slate-200 bg-slate-50 p-4 dark:border-gray-700 dark:bg-gray-900">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <p className="text-sm font-semibold text-slate-950 dark:text-white">Current rate</p>
                <p className="mt-2 text-2xl font-semibold text-slate-950 dark:text-white">
                  {loadingRate ? "Loading..." : `${formatUsd(1)} = ${formatNaira(rateInfo.rate)}`}
                </p>
                <p className="mt-2 text-xs text-slate-500 dark:text-slate-400">
                  Source: {rateInfo.source || "live"} · Updated: {rateUpdatedLabel}
                </p>
              </div>
              <Button type="button" variant="secondary" onClick={() => window.location.reload()}>
                <RefreshCcw className="h-4 w-4" />
                Refresh page data
              </Button>
            </div>
          </div>

          <form className="mt-6 space-y-5" onSubmit={saveRate}>
            <Field
              label="USD to NGN rate"
              type="number"
              min="0"
              step="0.01"
              value={rateInput}
              onChange={(event) => setRateInput(event.target.value)}
              hint="Example: 1500 means $1 displays as about ₦1,500."
            />
            <Button type="submit" variant="brand" className="w-full" disabled={savingRate || !rateInput.trim()}>
              <ShieldCheck className="h-4 w-4" />
              {savingRate ? "Saving rate..." : "Save exchange rate"}
            </Button>
          </form>
        </SurfaceCard>
      </div>

      <div className="grid gap-6 xl:grid-cols-2">
        <SurfaceCard>
          <SectionHeader eyebrow="MekFunded Support" title="Identity relink and suspension" description="Use the MekFunded identity document ID to relink support cases, suspend sync, or disable a customer identity after review." />
          <form className="mt-6 space-y-5" onSubmit={relinkIdentity}>
            <Field
              label="Identity document ID"
              type="text"
              placeholder="Mongo identity ID"
              value={identitySupportInput.identityId}
              onChange={(event) => setIdentitySupportInput({ ...identitySupportInput, identityId: event.target.value })}
              required
            />
            <Field
              label="New MekEdge user ID"
              type="text"
              placeholder="Target MekEdge user ID for relink"
              value={identitySupportInput.mekedgeUserId}
              onChange={(event) => setIdentitySupportInput({ ...identitySupportInput, mekedgeUserId: event.target.value })}
            />
            <label className="flex items-center gap-3 text-sm text-slate-600 dark:text-slate-300">
              <input
                type="checkbox"
                checked={identitySupportInput.activateDisabled}
                onChange={(event) => setIdentitySupportInput({ ...identitySupportInput, activateDisabled: event.target.checked })}
              />
              Reactivate previously disabled entitlements during relink
            </label>
            <div className="grid gap-3 sm:grid-cols-3">
              <Button type="submit" variant="brand" disabled={mekFundedLoading || !identitySupportInput.identityId.trim() || !identitySupportInput.mekedgeUserId.trim()}>
                <UserCog className="h-4 w-4" />
                {mekFundedLoading ? "Working..." : "Relink"}
              </Button>
              <Button type="button" variant="secondary" disabled={mekFundedLoading || !identitySupportInput.identityId.trim()} onClick={unlinkIdentity}>
                <ShieldOff className="h-4 w-4" />
                Unlink
              </Button>
              <Button type="button" variant="danger" disabled={mekFundedLoading || !identitySupportInput.identityId.trim()} onClick={disableIdentity}>
                <Ban className="h-4 w-4" />
                Disable
              </Button>
            </div>
          </form>
        </SurfaceCard>

        <SurfaceCard>
          <SectionHeader eyebrow="Claim Delivery" title="Entitlement control and claim delivery" description="Use the entitlement document ID to suspend or revoke a specific account, send from MekEdge SMTP, or mint a claim link for MekFunded to email externally." />
          <div className="mt-4 rounded-3xl border border-slate-200 bg-slate-50 p-4 text-sm text-slate-600 dark:border-gray-700 dark:bg-gray-900 dark:text-slate-300">
            The claim token must be generated by MekEdge because MekEdge validates it. MekFunded can still trigger or initiate the workflow, but this admin panel sends the email from MekEdge when mail is configured.
          </div>
          <div className="mt-6 space-y-5">
            <Field
              label="Entitlement document ID"
              type="text"
              placeholder="Mongo entitlement ID"
              value={entitlementSupportInput.entitlementId}
              onChange={(event) => setEntitlementSupportInput({ ...entitlementSupportInput, entitlementId: event.target.value })}
              required
            />
            <Field
              label="Claim email override"
              type="email"
              placeholder="Optional override recipient"
              value={entitlementSupportInput.email}
              onChange={(event) => setEntitlementSupportInput({ ...entitlementSupportInput, email: event.target.value })}
            />
            <Field
              label="Claim TTL hours"
              type="number"
              min="1"
              step="1"
              value={entitlementSupportInput.claimTtlHours}
              onChange={(event) => setEntitlementSupportInput({ ...entitlementSupportInput, claimTtlHours: event.target.value })}
              hint="168 hours means the claim link stays valid for 7 days."
            />
            <div className="grid gap-3 sm:grid-cols-4">
              <Button type="button" variant="brand" disabled={mekFundedLoading || !entitlementSupportInput.entitlementId.trim()} onClick={mintClaimLink}>
                <Copy className="h-4 w-4" />
                {mekFundedLoading ? "Working..." : "Mint Claim"}
              </Button>
              <Button type="button" variant="brand" disabled={mekFundedLoading || !entitlementSupportInput.entitlementId.trim()} onClick={sendClaimEmail}>
                <Mail className="h-4 w-4" />
                {mekFundedLoading ? "Working..." : "Send Claim"}
              </Button>
              <Button type="button" variant="secondary" disabled={mekFundedLoading || !entitlementSupportInput.entitlementId.trim()} onClick={disableEntitlement}>
                <ShieldOff className="h-4 w-4" />
                Disable
              </Button>
              <Button type="button" variant="danger" disabled={mekFundedLoading || !entitlementSupportInput.entitlementId.trim()} onClick={revokeEntitlement}>
                <Ban className="h-4 w-4" />
                Revoke
              </Button>
            </div>

            {mintedClaim?.url ? (
              <div className="rounded-3xl border border-slate-200 bg-slate-50 p-4 dark:border-gray-700 dark:bg-gray-900">
                <p className="text-sm font-semibold text-slate-900 dark:text-white">Minted claim payload</p>
                <p className="mt-2 text-xs text-slate-500 dark:text-slate-400">Use this when MekFunded sends the email outside MekEdge.</p>
                <div className="mt-3 space-y-3">
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-[0.24em] text-slate-400">Claim URL</p>
                    <p className="mt-1 break-all text-sm text-slate-700 dark:text-slate-200">{mintedClaim.url}</p>
                    <Button type="button" variant="secondary" className="mt-2" onClick={() => copyToClipboard(mintedClaim.url, "Claim URL")}>
                      <Copy className="h-4 w-4" />
                      Copy URL
                    </Button>
                  </div>
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-[0.24em] text-slate-400">Claim token</p>
                    <p className="mt-1 break-all text-sm text-slate-700 dark:text-slate-200">{mintedClaim.token}</p>
                    <Button type="button" variant="secondary" className="mt-2" onClick={() => copyToClipboard(mintedClaim.token, "Claim token")}>
                      <Copy className="h-4 w-4" />
                      Copy token
                    </Button>
                  </div>
                  <p className="text-xs text-slate-500 dark:text-slate-400">Expires at: {mintedClaim.expiresAt ? new Date(mintedClaim.expiresAt).toLocaleString() : "n/a"}</p>
                </div>
              </div>
            ) : null}
          </div>
        </SurfaceCard>
      </div>

      <SurfaceCard>
        <div className="flex flex-wrap items-center justify-between gap-4">
          <SectionHeader eyebrow="Visibility" title="Imported trades and sync health" description="Quickly inspect linked entitlements, imported MT5 trades, and recent audit events from one admin surface." />
          <Button type="button" variant="secondary" onClick={reloadImportVisibility} disabled={importVisibilityLoading}>
            <RefreshCcw className="h-4 w-4" />
            {importVisibilityLoading ? "Refreshing..." : "Refresh visibility"}
          </Button>
        </div>
        <div className="mt-4 grid gap-3 md:grid-cols-2 xl:grid-cols-[1.2fr_0.8fr]">
          <Field
            label="Search"
            type="text"
            placeholder="Search customer, login, asset, or audit action"
            value={visibilityQuery}
            onChange={(event) => {
              setVisibilityQuery(event.target.value);
              setPage("entitlements", 1);
              setPage("trades", 1);
              setPage("audits", 1);
            }}
          />
          <Field
            label="Status filter"
            type="text"
            placeholder="all, active, pending_claim, disabled, revoked"
            value={visibilityStatus}
            onChange={(event) => {
              setVisibilityStatus(event.target.value.trim() || "all");
              setPage("entitlements", 1);
            }}
            hint="Type a status value to narrow entitlements." 
          />
        </div>
        {importVisibilityError ? <p className="mt-4 text-sm font-medium text-rose-600 dark:text-rose-300">{importVisibilityError}</p> : null}
        <div className="mt-6 grid gap-4 md:grid-cols-3">
          <div className="rounded-3xl border border-slate-200 bg-slate-50 p-4 dark:border-gray-700 dark:bg-gray-900">
            <p className="text-xs uppercase tracking-[0.24em] text-slate-400">Entitlements</p>
            <p className="mt-2 text-2xl font-semibold text-slate-950 dark:text-white">{importVisibility?.entitlements?.length || 0}</p>
          </div>
          <div className="rounded-3xl border border-slate-200 bg-slate-50 p-4 dark:border-gray-700 dark:bg-gray-900">
            <p className="text-xs uppercase tracking-[0.24em] text-slate-400">Imported trades</p>
            <p className="mt-2 text-2xl font-semibold text-slate-950 dark:text-white">{importVisibility?.importedTrades?.length || 0}</p>
          </div>
          <div className="rounded-3xl border border-slate-200 bg-slate-50 p-4 dark:border-gray-700 dark:bg-gray-900">
            <p className="text-xs uppercase tracking-[0.24em] text-slate-400">Audit events</p>
            <p className="mt-2 text-2xl font-semibold text-slate-950 dark:text-white">{importVisibility?.auditLogs?.length || 0}</p>
          </div>
        </div>

        <div className="mt-6 grid gap-6 xl:grid-cols-3">
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.24em] text-slate-400">Recent entitlements</p>
            <div className="mt-3 space-y-3">
              {visibleEntitlements.map((item) => (
                <div key={item._id} className="rounded-3xl border border-slate-200 bg-white p-4 text-sm dark:border-gray-700 dark:bg-gray-950">
                  <p className="font-semibold text-slate-900 dark:text-white">{item.mekFundedCustomerId}</p>
                  <p className="mt-1 text-slate-500 dark:text-slate-400">{item.mt5Login} · {item.mt5Server}</p>
                  <p className="mt-1 text-xs uppercase tracking-[0.24em] text-slate-400">{item.status} · sync {item.tradeSyncEnabled ? "enabled" : "disabled"}</p>
                </div>
              ))}
              {!visibleEntitlements.length ? <p className="text-sm text-slate-500 dark:text-slate-400">No entitlements match the current filters.</p> : null}
            </div>
            <div className="mt-4 flex items-center justify-between gap-3 text-sm">
              <Button type="button" variant="secondary" disabled={visibilityPages.entitlements <= 1} onClick={() => setPage("entitlements", visibilityPages.entitlements - 1)}>Prev</Button>
              <span className="text-slate-500 dark:text-slate-400">Page {visibilityPages.entitlements}</span>
              <Button type="button" variant="secondary" disabled={visibleEntitlements.length < VISIBILITY_PAGE_SIZE} onClick={() => setPage("entitlements", visibilityPages.entitlements + 1)}>Next</Button>
            </div>
          </div>

          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.24em] text-slate-400">Recent imported trades</p>
            <div className="mt-3 space-y-3">
              {visibleTrades.map((item) => (
                <div key={item._id} className="rounded-3xl border border-slate-200 bg-white p-4 text-sm dark:border-gray-700 dark:bg-gray-950">
                  <p className="font-semibold text-slate-900 dark:text-white">{item.asset} · {item.direction}</p>
                  <p className="mt-1 text-slate-500 dark:text-slate-400">{item.externalAccountLogin} · {item.externalPositionId}</p>
                  <p className="mt-1 text-xs uppercase tracking-[0.24em] text-slate-400">{item.source} · v{item.syncVersion || 1}</p>
                </div>
              ))}
              {!visibleTrades.length ? <p className="text-sm text-slate-500 dark:text-slate-400">No imported trades match the current filters.</p> : null}
            </div>
            <div className="mt-4 flex items-center justify-between gap-3 text-sm">
              <Button type="button" variant="secondary" disabled={visibilityPages.trades <= 1} onClick={() => setPage("trades", visibilityPages.trades - 1)}>Prev</Button>
              <span className="text-slate-500 dark:text-slate-400">Page {visibilityPages.trades}</span>
              <Button type="button" variant="secondary" disabled={visibleTrades.length < VISIBILITY_PAGE_SIZE} onClick={() => setPage("trades", visibilityPages.trades + 1)}>Next</Button>
            </div>
          </div>

          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.24em] text-slate-400">Audit log viewer</p>
            <div className="mt-3 grid gap-3">
              <Field
                label="Action filter"
                type="text"
                placeholder="claim.link_existing_account, mt5_sync_completed, ..."
                value={auditActionFilter}
                onChange={(event) => {
                  setAuditActionFilter(event.target.value);
                  setPage("audits", 1);
                }}
              />
              <Field
                label="Outcome filter"
                type="text"
                placeholder="all, success, failure, blocked"
                value={auditOutcomeFilter}
                onChange={(event) => {
                  setAuditOutcomeFilter(event.target.value.trim() || "all");
                  setPage("audits", 1);
                }}
              />
              <Field
                label="Customer filter"
                type="text"
                placeholder="mekFundedCustomerId"
                value={auditCustomerFilter}
                onChange={(event) => {
                  setAuditCustomerFilter(event.target.value);
                  setPage("audits", 1);
                }}
              />
            </div>
            <div className="mt-3 space-y-3">
              {visibleAudits.map((item) => (
                <div key={item._id} className="rounded-3xl border border-slate-200 bg-white p-4 text-sm dark:border-gray-700 dark:bg-gray-950">
                  <p className="font-semibold text-slate-900 dark:text-white">{item.action}</p>
                  <p className="mt-1 text-slate-500 dark:text-slate-400">{item.outcome} · {item.actorType}</p>
                  <p className="mt-1 text-slate-500 dark:text-slate-400">{item.mekFundedCustomerId || "no customer id"}</p>
                  <p className="mt-1 text-xs uppercase tracking-[0.24em] text-slate-400">
                    {item.identityLinkId || "no identity"} · {item.entitlementId || "no entitlement"}
                  </p>
                  {item.message ? <p className="mt-2 text-xs leading-5 text-slate-500 dark:text-slate-400">{item.message}</p> : null}
                </div>
              ))}
              {!visibleAudits.length ? <p className="text-sm text-slate-500 dark:text-slate-400">No audit logs match the current filters.</p> : null}
            </div>
            <div className="mt-4 flex items-center justify-between gap-3 text-sm">
              <Button type="button" variant="secondary" disabled={visibilityPages.audits <= 1} onClick={() => setPage("audits", visibilityPages.audits - 1)}>Prev</Button>
              <span className="text-slate-500 dark:text-slate-400">Page {visibilityPages.audits}</span>
              <Button type="button" variant="secondary" disabled={visibleAudits.length < AUDIT_PAGE_SIZE} onClick={() => setPage("audits", visibilityPages.audits + 1)}>Next</Button>
            </div>
          </div>
        </div>
      </SurfaceCard>
    </div>
  );
}

export default AdminTools;
